// HighlightPicker — popover content for setting a booking's highlight.
//
// PATCH /api/v1/collections/bookings/:enc_id { highlight_type: 'none' | 'full' | 'notes' }
// is the only schema PATCH allows beyond paid/collected. Used by the
// diary row's Highlight icon.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Highlighter } from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { Popover } from '../../../components/ui/Popover/Popover';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useToast } from '../../../hooks/useToast';
import type { HighlightType } from '../../../types/api';
import styles from './HighlightPicker.module.css';

const OPTIONS: Array<{ value: HighlightType; label: string; description: string }> = [
  { value: 'none', label: 'None', description: 'Remove any highlight' },
  { value: 'full', label: 'Full row', description: 'Highlight the whole row' },
  { value: 'notes', label: 'Notes only', description: 'Highlight only the notes column' },
];

export interface HighlightPickerProps {
  encId: string;
  current: HighlightType;
  /** Optional invalidation key — pass the diary week key so the row
   *  re-renders after PATCH. */
  invalidateKeys?: ReadonlyArray<readonly unknown[]>;
  ariaLabel?: string;
}

export function HighlightPicker({
  encId,
  current,
  invalidateKeys = [],
  ariaLabel = 'Highlight booking',
}: HighlightPickerProps): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (highlight_type: HighlightType) => {
      await apiClient.patch(endpoints.bookings.detail(encId), { highlight_type });
    },
    onSuccess: async () => {
      await Promise.all([
        ...invalidateKeys.map((key) =>
          queryClient.invalidateQueries({ queryKey: key as unknown[] }),
        ),
        queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all }),
      ]);
      toast.success('Highlight updated');
    },
    onError: (err) => {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not update highlight.'
        : 'Could not update highlight.';
      toast.error(message);
    },
  });

  return (
    <Popover
      ariaLabel="Highlight options"
      trigger={
        <Button
          variant="ghost"
          size="sm"
          icon={Highlighter}
          aria-label={ariaLabel}
          disabled={mutation.isPending}
        />
      }
    >
      <ul className={styles.list}>
        {OPTIONS.map((opt) => (
          <li key={opt.value}>
            <button
              type="button"
              className={
                opt.value === current ? styles.itemActive : styles.item
              }
              onClick={() => mutation.mutate(opt.value)}
              disabled={mutation.isPending}
            >
              <span className={styles.itemLabel}>{opt.label}</span>
              <span className={styles.itemDesc}>{opt.description}</span>
            </button>
          </li>
        ))}
      </ul>
    </Popover>
  );
}
